import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { DistrictUpdateInput } from "@/modules/geo/domain/contracts";
import { QueryState } from "@/shared/ui/QueryState";
import { Button, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, HelpCircleIcon } from "lucide-react";
import {
  useDistrictQuery,
  useProvincesQuery,
  useUpdateDistrict,
} from "../api/queries";
import { DistrictFormTour, useDistrictFormTour } from "../tour";
import { DistrictForm } from "../ui/DistrictForm";

export function DistrictEditPage() {
  const nav = useNavigate({ from: "/app/geo/districts/$id/edit" });
  const { id } = useParams({ from: "/app/geo/districts/$id/edit" });
  const { data, ...result } = useDistrictQuery(id);
  const updateDistrict = useUpdateDistrict(id);
  const provincesQuery = useProvincesQuery({ limit: 500, offset: 0 });
  const provinces = provincesQuery.data?.data ?? [];
  const { run, handleJoyrideCallback, startTour } = useDistrictFormTour();

  const district = data;
  const districtName =
    district && typeof district.name === "object" && district.name !== null
      ? (district.name as { lo?: string })?.lo ??
        (district.name as { en?: string })?.en ??
        (district as { code?: string })?.code
      : (district as { name?: string })?.name ??
        (district as { code?: string })?.code ??
        id ??
        "ແກ້ໄຂ";

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ເມືອງ", to: "/app/geo/districts" },
            { label: districtName },
          ]}
        />
      </Header>
      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="flex items-center font-bold text-2xl tracking-tight">
              ແກ້ໄຂເມືອງ{" "}
              <Button
                variant="ghost"
                size="icon"
                onClick={startTour}
                className="ml-2 size-6"
                title="ເລີ່ມທົດລອງການນຳທາງ"
              >
                <HelpCircleIcon className="h-4 w-4" />
              </Button>
            </h2>
            <p className="text-muted-foreground">ປັບລາຍລະອຽດຂອງເມືອງ.</p>
          </div>
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/geo/districts" } as any)}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
        </div>
        <QueryState
          result={result}
          title="ກຳລັງໂຫຼດເມືອງ"
          description="ກຳລັງດຶງລາຍລະອຽດ"
          variant="fullscreen"
        >
          {!district ? null : (
            <div className="mt-6 rounded-xl border bg-card p-6">
              <DistrictForm
                provinces={provinces}
                initialValues={{
                  provinceId: district.provinceId,
                  nameLo: (district.name as { lo?: string })?.lo ?? "",
                  nameTh: (district.name as { th?: string })?.th ?? "",
                  nameEn: (district.name as { en?: string })?.en ?? "",
                  enabled: district.enabled,
                  sortOrder: district.sortOrder,
                }}
                onSubmit={async (values: DistrictUpdateInput) => {
                  try {
                    await updateDistrict.mutateAsync(values);
                    toast.success("ແກ້ໄຂເມືອງສຳເລັດ", {
                      description: "ເມືອງຖືກອັບເດດສຳເລັດແລ້ວ",
                    });
                    nav({ to: "/app/geo/districts" } as any);
                  } catch {
                    // Error handling is done by mutation
                  }
                }}
                submitting={updateDistrict.isPending}
              />
            </div>
          )}
        </QueryState>
        <DistrictFormTour run={run} onCallback={handleJoyrideCallback} />
      </Main>
    </>
  );
}
